import type { User } from '@keboola/api-client/developerPortal/types';

const contains = (haystack: string | null | undefined, needle: string): boolean =>
  (haystack ?? '').toLowerCase().includes(needle);

/**
 * Filters a vendor's developers by name or email.
 *
 * - `name` is missing on users invited but not yet accepted.
 */
export const filterUsers = (users: readonly User[], query: string): User[] => {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...users];
  return users.filter((user) => contains(user.username, needle) || contains(user.name, needle));
};

/** Filters service accounts by name or description — the two columns the table renders. */
export const filterServiceAccounts = (accounts: readonly User[], query: string): User[] => {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...accounts];
  return accounts.filter(
    (account) => contains(account.username, needle) || contains(account.description, needle),
  );
};

/**
 * Whether a member row is the signed-in user, which turns "Remove" into "Leave".
 *
 * - Case-insensitive: Cognito's `email` claim and apps-api's `username` can differ in case.
 * - A miss offers "Remove" on the user's own row, skipping the token rotation leaving needs.
 */
export const isSelf = (user: Pick<User, 'username'>, email: string | null): boolean =>
  email !== null && user.username.toLowerCase() === email.toLowerCase();
